import { locale, t } from './i18n';
import { money } from './state';
import type {
    LoyaltyCriterion,
    LoyaltyMultiplier,
    LoyaltyQuest,
} from './types';

/**
 * «Квести й досягнення» in words a player reads (Черняк, 2026-10-15): «Зіграти 5 годин у зоні
 * VIP», «1,5 / 5 год», «до 22:00». Built from the conditions the server sends, never from their
 * technical names.
 */

/** One, few, many — the three Slavic forms; English takes the first two. */
function plural(n: number, one: string, few: string, many: string): string {
    const whole = Math.abs(n) % 1 === 0;

    if (locale.value === 'en') {
        return n === 1 ? one : few;
    }

    if (!whole) {
        return few;
    }

    const lastTwo = Math.abs(n) % 100;
    const last = Math.abs(n) % 10;

    if (last === 1 && lastTwo !== 11) {
        return one;
    }

    return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
        ? few
        : many;
}

/** «1,5» / «12» — a quantity as the player's language writes it. */
export function amount(value: number): string {
    return money(value, locale.value);
}

function hoursWord(n: number): string {
    return plural(n, t('годину'), t('години'), t('годин'));
}

function timesWord(n: number): string {
    return plural(n, t('раз'), t('рази'), t('разів'));
}

const DAY_KEYS = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** «Пн, Ср, Пт» — Monday first, the way a week reads here. */
function daysText(days: number[]): string {
    return [...days]
        .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
        .map((d) => t(DAY_KEYS[d] ?? ''))
        .join(', ');
}

/** «Зіграти 5 годин у зоні VIP по Пн, Пт з 18:00 до 22:00». */
export function conditionText(c: LoyaltyCriterion): string {
    const n = c.qty;
    let text: string;

    switch (c.metric) {
        case 'hours':
            text = t('Зіграти {n} {unit}', {
                n: amount(n),
                unit: hoursWord(n),
            });
            break;
        case 'visits':
            text = t('Прийти {n} {unit}', { n: amount(n), unit: timesWord(n) });
            break;
        case 'spend':
            text = t('Зробити покупки на {n} ₴', { n: amount(n) });
            break;
        case 'product':
            text =
                n > 1
                    ? t('Купити «{product}» × {n}', {
                          product: c.product ?? t('товар'),
                          n: amount(n),
                      })
                    : t('Купити «{product}»', {
                          product: c.product ?? t('товар'),
                      });
            break;
        default:
            text = t('Виконати умову');
    }

    if (c.zone) {
        text += ` ${t('у зоні {zone}', { zone: c.zone })}`;
    }

    if (c.days.length && c.days.length < 7) {
        text += ` ${t('у {days}', { days: daysText(c.days) })}`;
    }

    // One side alone is a real rule too: «Сова» counts visits after 23:00 and had lost the
    // «після 23:00» (2026-10-15).
    const from = c.from?.slice(0, 5);
    const to = c.to?.slice(0, 5);

    if (from && to) {
        text += ` ${t('з {from} до {to}', { from, to })}`;
    } else if (from) {
        text += ` ${t('після {time}', { time: from })}`;
    } else if (to) {
        text += ` ${t('до {time}', { time: to })}`;
    }

    return text;
}

/** «1,5 / 5 год», «120 / 200 ₴», «1 / 3». */
export function progressText(c: LoyaltyCriterion): string {
    const pair = `${amount(c.current)} / ${amount(c.qty)}`;

    switch (c.metric) {
        case 'hours':
            return `${pair} ${t('год')}`;
        case 'spend':
            return `${pair} ₴`;
        case 'product':
            return `${pair} ${t('шт')}`;
        default:
            return pair;
    }
}

/** «до 22:00», «скоро о 18:00», «сьогодні» — when a quest of today counts. */
export function questWhen(quest: LoyaltyQuest): string {
    if (quest.state === 'upcoming') {
        return t('о {time}', { time: quest.time_from ?? '00:00' });
    }

    if (quest.time_to && quest.time_to !== '23:59') {
        return quest.time_from && quest.state === 'done'
            ? `${quest.time_from}–${quest.time_to}`
            : t('до {time}', { time: quest.time_to });
    }

    return t('сьогодні');
}

/** «×2 XP», «+20% XP». */
export function multiplierText(m: LoyaltyMultiplier, currency: string): string {
    return m.mode === 'factor'
        ? `×${amount(m.value)} ${currency}`
        : `+${amount(m.value)}% ${currency}`;
}

/** The four rarities' names and their colours, the panel's own. */
export const RARITY: Record<string, { label: string; color: string }> = {
    common: { label: 'Звичайне', color: '#9aa3ad' },
    rare: { label: 'Рідкісне', color: '#2997ff' },
    epic: { label: 'Епічне', color: '#b26bff' },
    legendary: { label: 'Легендарне', color: '#ffb800' },
};

export function rarity(key: string): { label: string; color: string } {
    const r = RARITY[key] ?? RARITY.common;

    return { label: t(r.label), color: r.color };
}
